import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X, ChevronDown } from "lucide-react";

const SERVICE_ITEMS = [
  { name: "Agentic AI", path: "/services/agentic-ai", targetId: "service-agentic-ai" },
  { name: "Legacy Data Migration", targetId: "service-legacy-data-migration" },
  { name: "Automation Audit", targetId: "service-automation-audit" },
  { name: "Automated Workflows", targetId: "service-automated-workflows" },
  { name: "Digital Workers", targetId: "service-digital-workers" },
  { name: "ERP Implementations", targetId: "service-erp-implementations" },
];

const StackNavbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState("Agentic AI");
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
      setHoveredService("Agentic AI");
    }, 180);
  };

  const handleNavClick = (hash) => {
    setOpen(false);
    setServicesOpen(false);
    navigate(`/what-we-do${hash}`);
  };

  const handleServiceClick = (item) => {
    setOpen(false);
    setServicesOpen(false);
    if (item.path) {
      navigate(item.path);
    } else {
      navigate(`/what-we-do#${item.targetId}`);
    }
  };

  const handleBackClick = () => {
    setOpen(false);
    setServicesOpen(false);
    navigate("/what-we-do");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#010710]/95 backdrop-blur-md border-b border-white/5 shadow-lg shadow-black/40"
          : "bg-[#010710] border-b border-white/[0.04]"
      }`}
    >
      <div className="mx-auto flex h-[76px] sm:h-[80px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
        {/* Brand Logo */}
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
        >
          <img
            src="/images/simpel-icon-mark.png"
            alt="Simpel"
            className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="flex flex-col justify-center">
            <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
              Simpel
            </span>
            <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
              AUTOMATION SIMPLIFIED
            </span>
          </div>
        </Link>

        {/* Desktop Nav Items */}
        <div className="hidden md:flex items-center gap-7 lg:gap-9">
          {/* Services with Dropdown matching screenshot */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={() => handleNavClick("#services")}
              className={`inline-flex items-center gap-1.5 text-[14px] sm:text-[15px] font-medium transition-colors cursor-pointer py-2 ${
                servicesOpen ? "text-[#00D2EE]" : "text-white/85 hover:text-white"
              }`}
            >
              <span>Services</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  servicesOpen ? "rotate-180 text-[#00D2EE]" : "text-white/60"
                }`}
              />
            </button>

            {/* Dropdown Container */}
            {servicesOpen && (
              <div
                className="absolute top-full left-0 pt-2 z-50"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className="w-[310px] sm:w-[330px] rounded-[22px] bg-[#212121]/70 backdrop-blur-xl p-3 shadow-[0_24px_60px_rgba(0,0,0,0.9)] border border-white/10 flex flex-col gap-1.5 select-none animate-in fade-in slide-in-from-top-2 duration-150"
                  style={{
                    backgroundColor: "rgba(33, 33, 33, 0.70)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    boxShadow: "0 20px 50px -10px rgba(0, 0, 0, 0.95), 0 0 1px 1px rgba(255, 255, 255, 0.08)",
                  }}
                >
                  {SERVICE_ITEMS.map((item) => {
                    const isItemActive = hoveredService === item.name;
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onMouseEnter={() => setHoveredService(item.name)}
                        onClick={() => handleServiceClick(item)}
                        className={`w-full text-left px-4 py-3 rounded-[12px] text-[15px] sm:text-[16px] font-semibold tracking-[-0.01em] transition-all duration-150 cursor-pointer ${
                          isItemActive
                            ? "text-white shadow-[0_4px_16px_rgba(0,210,238,0.35)]"
                            : "text-white/95 hover:text-white"
                        }`}
                        style={{
                          background: isItemActive
                            ? "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)"
                            : "transparent",
                        }}
                      >
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => handleNavClick("#products")}
            className="text-[14px] sm:text-[15px] font-medium text-white/85 hover:text-white transition-colors cursor-pointer"
          >
            Products
          </button>
          <button
            onClick={() => handleNavClick("#stacks")}
            className="text-[14px] sm:text-[15px] font-medium text-white/85 hover:text-white transition-colors cursor-pointer"
          >
            Stacks
          </button>

          {/* Back to What we do button */}
          <button
            onClick={handleBackClick}
            className="group inline-flex items-center gap-2 rounded-full px-5 sm:px-6 py-2.5 sm:py-2.5 text-[13.5px] sm:text-[14px] font-bold text-[#010710] shadow-[0_0_24px_rgba(0,210,238,0.45)] hover:shadow-[0_0_32px_rgba(0,210,238,0.7)] hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer ml-1 lg:ml-2"
            style={{
              background: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
            }}
          >
            <span>Back to What we do</span>
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={2.3}
            />
          </button>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className="md:hidden text-white/80 hover:text-white p-2"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#010710]/98 px-6 pb-6 pt-3 shadow-2xl backdrop-blur-xl">
          <div className="flex flex-col space-y-2">
            <div>
              <button
                onClick={() => setMobileServicesOpen((prev) => !prev)}
                className="w-full flex items-center justify-between py-2.5 text-left text-[15px] font-medium text-white/85 hover:text-white"
              >
                <span>Services</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    mobileServicesOpen ? "rotate-180 text-[#00D2EE]" : "text-white/60"
                  }`}
                />
              </button>
              {mobileServicesOpen && (
                <div
                  className="ml-2 pl-3 border-l border-white/15 flex flex-col gap-1 py-2 px-1 rounded-xl my-1 backdrop-blur-xl"
                  style={{
                    backgroundColor: "rgba(33, 33, 33, 0.70)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                  }}
                >
                  {SERVICE_ITEMS.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleServiceClick(item)}
                      className="text-left py-2 px-3 rounded-lg text-[14px] text-white/90 hover:text-white hover:bg-white/5"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavClick("#products")}
              className="py-2.5 text-left text-[15px] font-medium text-white/85 hover:text-white"
            >
              Products
            </button>
            <button
              onClick={() => handleNavClick("#stacks")}
              className="py-2.5 text-left text-[15px] font-medium text-white/85 hover:text-white"
            >
              Stacks
            </button>

            <div className="pt-3">
              <button
                onClick={handleBackClick}
                className="w-full flex items-center justify-center gap-2 rounded-full py-3 text-[14px] font-bold text-[#010710] shadow-[0_0_24px_rgba(0,210,238,0.45)]"
                style={{
                  background: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
                }}
              >
                <span>Back to What we do</span>
                <ArrowRight className="h-4 w-4" strokeWidth={2.3} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default StackNavbar;
